import { type ProductSummary, formatBRL } from '@deizi/shared';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Combobox } from '../../components/Combobox';
import { Icon } from '../../components/Icon';
import { PageHead } from '../../components/PageHead';
import { ErrorBox } from '../../components/States';
import {
  productParams,
  useCategories,
  useProducts,
  type ProductFilters,
} from '../../lib/products-api';
import { readPref, writePref } from '../../lib/storage';
import { useIsPhone } from '../../lib/use-media';
import { ArchiveDialog, useProductActions } from './actions';
import { Cover, StatusBadge } from './common';
import { NewProductDialog } from './NewProductDialog';
import { ExportCsvButton } from '../../components/ExportCsv';

const SEGS: [ProductFilters['segment'], string][] = [
  ['all', 'Todos'],
  ['active', 'Ativos'],
  ['prototype', 'Protótipo'],
  ['idea', 'Ideia'],
  ['custom', 'Sob encomenda'],
  ['archived', 'Arquivados'],
];
const MATERIALS = ['PLA', 'PLA Silk', 'PLA Matte', 'PETG', 'ABS', 'ASA', 'TPU', 'PC'];

function sub(p: ProductSummary): string {
  if (p.status === 'archived' && p.archivedAt) {
    return `${p.sku} · arquivado em ${new Date(p.archivedAt).toLocaleDateString('pt-BR')}`;
  }
  const parts = [p.custom && p.customer ? p.customer.name : p.sku];
  parts.push(p.variantCount === 1 ? '1 variação' : `${p.variantCount} variações`);
  if (p.materials.length) parts.push(p.materials.join(', '));
  if (p.stockUnits) parts.push(`${p.stockUnits} pronta entrega`);
  if (p.origin === 'third_party') parts.push('projeto de terceiro');
  return parts.join(' · ');
}

const alertColor = (level: 'warn' | 'err') => (level === 'err' ? 'var(--err)' : 'var(--warn)');

export function ProductsPage() {
  const phone = useIsPhone();
  const navigate = useNavigate();
  const [segment, setSegment] = useState<ProductFilters['segment']>('all');
  const [category, setCategory] = useState('');
  const [material, setMaterial] = useState('');
  const [view, setView] = useState<'grid' | 'table'>(() =>
    readPref('products-view') === 'table' ? 'table' : 'grid',
  );
  const [menu, setMenu] = useState<string | null>(null);
  const [archiving, setArchiving] = useState<ProductSummary | null>(null);
  const [creating, setCreating] = useState(false);
  const list = useProducts({ segment, category, material });
  const categories = useCategories();
  const actions = useProductActions();
  const rows = list.data?.data ?? [];
  const counts = list.data?.counts;
  const total = counts ? counts.all + counts.archived : 0;
  const filtered = !!category || !!material;

  const changeView = (v: 'grid' | 'table') => {
    writePref('products-view', v);
    setView(v);
  };
  const clear = () => {
    setSegment('all');
    setCategory('');
    setMaterial('');
  };
  const summary = (() => {
    const n = rows.length;
    let s = `${n} ${n === 1 ? 'produto' : 'produtos'}`;
    s +=
      segment === 'all'
        ? ' (sem os arquivados)'
        : ` · ${SEGS.find(([k]) => k === segment)?.[1].toLowerCase() ?? ''}`;
    if (category) s += ` · ${category}`;
    if (material) s += ` · ${material}`;
    return s;
  })();

  const newButton = (
    <div className="row wrap" style={{ gap: 10 }}>
      <ExportCsvButton dataset="produtos" params={productParams({ segment, category, material })} />
      <button type="button" className="btn p" onClick={() => setCreating(true)}>
        <Icon name="plus" size={18} />
        Produto
      </button>
    </div>
  );
  const dialogs = (
    <>
      {creating ? (
        <NewProductDialog
          onClose={() => setCreating(false)}
          onCreated={(p) => {
            setCreating(false);
            void navigate(`/produtos/${p.id}`);
          }}
        />
      ) : null}
      {archiving ? (
        <ArchiveDialog
          name={archiving.name}
          onClose={() => setArchiving(null)}
          onConfirm={() => {
            actions.archive.mutate(archiving.id);
            setArchiving(null);
          }}
        />
      ) : null}
    </>
  );

  if (list.error && !list.data) {
    return (
      <>
        <PageHead crumb="Catálogo" title="Produtos" actions={newButton} />
        <div className="card">
          <ErrorBox
            title="Não foi possível carregar os produtos"
            onRetry={() => void list.refetch()}
          />
        </div>
      </>
    );
  }

  if (list.data && total === 0 && !filtered) {
    return (
      <>
        <PageHead crumb="Catálogo" title="Produtos" actions={newButton} />
        <div className="card">
          <div className="empty">
            <div className="emptyart" />
            <h2>Nenhum produto ainda</h2>
            <p>
              Cadastre um produto para guardar perfis de impressão, fotos e preços por canal num
              lugar só.
            </p>
            <button type="button" className="btn p" onClick={() => setCreating(true)}>
              Cadastrar primeiro produto
            </button>
          </div>
        </div>
        {dialogs}
      </>
    );
  }

  const card = (p: ProductSummary) => (
    <div
      key={p.id}
      className={p.status === 'archived' ? 'card pcard arch' : 'card pcard'}
      style={{ borderColor: p.marginAlert?.level === 'warn' ? 'var(--warn)' : undefined }}
    >
      <Link to={`/produtos/${p.id}`}>
        <Cover url={p.coverUrl} height={160} />
        <div className="row between" style={{ gap: 8, alignItems: 'flex-start' }}>
          <h3 style={{ minWidth: 0 }} className="trunc">
            {p.name}
          </h3>
          <span className="row" style={{ gap: 4, flex: '0 0 auto' }}>
            <StatusBadge status={p.status} custom={p.custom} />
          </span>
        </div>
        {p.marginAlert ? (
          <p
            className="xs row"
            style={{ gap: 4, color: alertColor(p.marginAlert.level), fontWeight: 500 }}
          >
            <Icon name="alert" size={14} />
            {p.marginAlert.text}
          </p>
        ) : null}
        <p className="sm muted sub">{sub(p)}</p>
        <div className="row between">
          <span className="xs muted">{p.fromPriceCentavos !== null ? 'a partir de' : ''}</span>
          <span className="fr tab" style={{ fontSize: 18 }}>
            {p.fromPriceCentavos !== null ? formatBRL(p.fromPriceCentavos) : 'sem preço'}
          </span>
        </div>
      </Link>
      <button
        type="button"
        className="kebab"
        aria-label={`Mais ações para ${p.name}`}
        aria-haspopup="menu"
        aria-expanded={menu === p.id}
        onClick={() => setMenu(menu === p.id ? null : p.id)}
      >
        <Icon name="dots" size={18} />
      </button>
      {menu === p.id ? (
        <>
          <button
            type="button"
            aria-label="Fechar menu"
            style={{
              position: 'fixed',
              inset: 0,
              background: 'transparent',
              border: 0,
              zIndex: 10,
            }}
            onClick={() => setMenu(null)}
          />
          <div className="menu pmenu" role="menu" aria-label="Ações">
            <Link role="menuitem" to={`/produtos/${p.id}`}>
              Abrir
            </Link>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenu(null);
                actions.duplicate.mutate(p.id);
              }}
            >
              Duplicar
            </button>
            {p.status === 'archived' ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenu(null);
                  actions.unarchive.mutate(p.id);
                }}
              >
                Desarquivar
              </button>
            ) : (
              <button
                type="button"
                role="menuitem"
                style={{ color: 'var(--err)' }}
                onClick={() => {
                  setMenu(null);
                  setArchiving(p);
                }}
              >
                Arquivar
              </button>
            )}
          </div>
        </>
      ) : null}
    </div>
  );

  return (
    <>
      <PageHead crumb="Catálogo" title="Produtos" actions={newButton} />
      <div className="row wrap" style={{ gap: 10 }}>
        <div className="seg" role="tablist" aria-label="Situação" style={{ flexWrap: 'wrap' }}>
          {SEGS.map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={segment === k}
              className={segment === k ? 'on' : ''}
              onClick={() => setSegment(k)}
            >
              {label}{' '}
              <span className="tab" style={{ opacity: 0.75 }}>
                {counts?.[k] ?? 0}
              </span>
            </button>
          ))}
        </div>
        <Combobox
          ariaLabel="Categoria"
          style={{ width: 220 }}
          value={category}
          options={[
            { value: '', label: 'Todas as categorias', pinned: 'top' },
            ...(categories.data?.data ?? []).map((c) => ({
              value: c,
              label: c,
              icon: <Icon name="tag" size={16} />,
            })),
          ]}
          onChange={setCategory}
        />
        <select
          className="input"
          aria-label="Material"
          style={{ width: 170 }}
          value={material}
          onChange={(e) => setMaterial(e.target.value)}
        >
          <option value="">Todos os materiais</option>
          {MATERIALS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <div className="grow" />
        {phone ? null : (
          <div className="seg" role="radiogroup" aria-label="Visualização">
            <button
              type="button"
              role="radio"
              aria-checked={view === 'grid'}
              aria-label="Grade"
              className={view === 'grid' ? 'on' : ''}
              onClick={() => changeView('grid')}
            >
              <Icon name="box" size={16} />
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={view === 'table'}
              aria-label="Tabela"
              className={view === 'table' ? 'on' : ''}
              onClick={() => changeView('table')}
            >
              <Icon name="menu" size={16} />
            </button>
          </div>
        )}
      </div>
      <p className="sm muted" aria-live="polite">
        {summary}
      </p>
      {!list.data ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))',
            gap: 16,
          }}
          aria-busy="true"
        >
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="skel" style={{ height: 262 }} />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="card sunk">
          <div className="empty" style={{ padding: '28px 16px' }}>
            <h3>Nenhum produto com esses filtros</h3>
            <p className="sm">Tente outra categoria ou material, ou veja todos os status.</p>
            <button type="button" className="btn sm" onClick={clear}>
              Limpar filtros
            </button>
          </div>
        </div>
      ) : view === 'table' && !phone ? (
        <div className="card flat">
          <div className="gt">
            <div
              className="gtr head"
              style={{ gridTemplateColumns: '2.2fr 1fr 1.2fr .8fr .9fr 1fr 1.6fr' }}
            >
              <span>Produto</span>
              <span>SKU</span>
              <span>Situação</span>
              <span className="num">Variações</span>
              <span className="num">Pronta entrega</span>
              <span className="num">A partir de</span>
              <span>Margem</span>
            </div>
            {rows.map((p) => (
              <Link
                key={p.id}
                to={`/produtos/${p.id}`}
                className={p.status === 'archived' ? 'gtr arch' : 'gtr'}
                style={{ gridTemplateColumns: '2.2fr 1fr 1.2fr .8fr .9fr 1fr 1.6fr' }}
              >
                <span className="row" style={{ gap: 10, minWidth: 0 }}>
                  <span style={{ width: 40, flex: '0 0 40px' }}>
                    <Cover url={p.coverUrl} height={40} label="" />
                  </span>
                  <span className="trunc" style={{ fontWeight: 500 }}>
                    {p.name}
                  </span>
                </span>
                <span className="tab sm">{p.sku}</span>
                <span className="row" style={{ gap: 4 }}>
                  <StatusBadge status={p.status} custom={p.custom} />
                </span>
                <span className="num">{p.variantCount}</span>
                <span className="num">{p.stockUnits ? `${p.stockUnits} un.` : '—'}</span>
                <span className="num" style={{ fontWeight: 600 }}>
                  {p.fromPriceCentavos !== null ? formatBRL(p.fromPriceCentavos) : '—'}
                </span>
                <span
                  className="sm"
                  style={{ color: p.marginAlert ? alertColor(p.marginAlert.level) : 'var(--ink2)' }}
                >
                  {p.marginAlert?.text ?? '—'}
                </span>
              </Link>
            ))}
          </div>
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: phone ? '1fr' : 'repeat(auto-fill,minmax(240px,1fr))',
            gap: 16,
          }}
        >
          {rows.map(card)}
        </div>
      )}
      {dialogs}
    </>
  );
}
